/* eslint-disable react/no-array-index-key */
import Image from 'common/components/Image'
import Skeleton from 'common/components/Skeleton'
import Typography from 'common/components/Typography'
import { mediaflexCdn } from 'lib/cdn'
import { useCallback, useEffect } from 'react'
import useInView from 'react-cool-inview'
import { useTranslation } from 'react-i18next'
import { useStoreActions, useStoreState } from 'stores/hooks'
import styled, { keyframes } from 'styled-components'

const Container = styled.section`
  width: 100%;
  padding: 48px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Content = styled.div`
  width: 100%;
  max-width: 1180px;
`

const TitleWrapper = styled.div`
  text-align: center;
  margin-bottom: 32px;
`

const scroll = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`

const LogoStrip = styled.div`
  display: flex;
  overflow: hidden;
  position: relative;
  width: 100%;
  &:hover > div {
    animation-play-state: paused;
  }
`

const LogoTrack = styled.div`
  display: flex;
  gap: 20px;
  animation: ${scroll} 28s linear infinite;
  align-items: center;
`

const LogoItem = styled.div`
  width: 160px;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  transition: transform 0.3s ease, filter 0.3s ease;
  filter: grayscale(1) contrast(0.9);
  padding-left: 20px;

  &:not(:first-child) {
    border-left: 1px solid rgba(0, 0, 0, 0.12);
  }

  &:hover {
    transform: translateY(-4px) scale(1.01);
    filter: none;
  }
`

function Partners({
  sectionTitle,
}: {
  sectionTitle?: string
}): JSX.Element {
  const { t, i18n } = useTranslation('home')
  const skeletonCount = useCallback((x) => new Array(x).fill(0), [])
  const { data, isLoading } = useStoreState((state) => state.partners)
  const { getPartners } = useStoreActions(
    (actions) => actions.partners,
  )

  const { observe, inView } = useInView<HTMLDivElement>({
    unobserveOnEnter: true,
  })

  useEffect(() => {
    if (inView) {
      getPartners()
    }
  }, [getPartners, inView, i18n.language])
  return (
    <>
      {data ? (
        <Container data-testid="partners-test" ref={observe}>
          <Content>
            <TitleWrapper>
              <Typography type="bh2" textAlign="center">
                {sectionTitle || t('PARTNERS_TITLE')}
              </Typography>
            </TitleWrapper>
            {isLoading ? (
              <LogoStrip>
                {skeletonCount(8).map((_, idx) => (
                  <Skeleton
                    key={`idx-partner-skeleton-${idx}`}
                    height="107px"
                    marginLeft="20px"
                    marginRight="20px"
                  />
                ))}
              </LogoStrip>
            ) : (
              <LogoStrip>
                <LogoTrack>
                  {data.concat(data).map((partner, index) => (
                    <LogoItem
                      key={`${partner.name_shop || index}-${index}`}
                    >
                      <Image
                        lazy
                        height="107px"
                        objectFit="scale-down"
                        src={mediaflexCdn(
                          `f=auto,h=150/${partner.logo_url}`,
                        )}
                        alt={partner.name_shop}
                      />
                    </LogoItem>
                  ))}
                </LogoTrack>
              </LogoStrip>
            )}
          </Content>
        </Container>
      ) : null}
    </>
  )
}

export default Partners
